import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSendLocation: (lat: number, lng: number, address: string) => void;
}

export function LocationPicker({ isOpen, onClose, onSendLocation }: LocationPickerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<{ lat: number; lng: number; address: string } | null>(null);

  const detectLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          // Reverse geocode via OpenStreetMap Nominatim (free, no key)
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await res.json();
          const address = data.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setLocation({ lat: latitude, lng: longitude, address });
        } catch {
          setLocation({ lat: latitude, lng: longitude, address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` });
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Location access denied');
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleSend = () => {
    if (location) {
      onSendLocation(location.lat, location.lng, location.address);
      setLocation(null);
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); setLocation(null); setError(null); } }}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-center">Share Location</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col items-center gap-4">
          {!location && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-3"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Navigation className="h-7 w-7 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground text-center max-w-[240px]">
                Share your current location with this contact
              </p>
              <Button onClick={detectLocation} className="rounded-full px-6">
                <MapPin className="h-4 w-4 mr-2" />
                Detect My Location
              </Button>
              {error && <p className="text-xs text-destructive">{error}</p>}
            </motion.div>
          )}

          {loading && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Finding your location…</p>
            </div>
          )}

          {location && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-sm"
            >
              {/* Map preview tile */}
              <div className="rounded-2xl overflow-hidden border border-border/50 mb-3">
                <img
                  src={`https://tile.openstreetmap.org/${Math.min(16, 16)}/${Math.floor(((location.lng + 180) / 360) * Math.pow(2, 16))}/${Math.floor(((1 - Math.log(Math.tan(location.lat * Math.PI / 180) + 1 / Math.cos(location.lat * Math.PI / 180)) / Math.PI) / 2) * Math.pow(2, 16))}.png`}
                  alt="Map preview"
                  className="w-full h-32 object-cover bg-muted"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <div className="p-3 bg-card">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground leading-snug line-clamp-2">{location.address}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setLocation(null); }} className="flex-1 rounded-full">
                  Retry
                </Button>
                <Button onClick={handleSend} className="flex-1 rounded-full">
                  Share Location
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
